import { useState, useEffect, useCallback } from 'react';
import { resolveTenant, listTracks, listVaults, listFans } from '../services/api';
import { useAuthStore } from '../store/authStore';

export interface OnboardingProgress {
  hasProfileImage: boolean;
  hasVerifiedTrack: boolean;
  hasPublishedVault: boolean;
  hasAtLeastOneFan: boolean;
  completedSteps: number;
  totalSteps: number;
  isComplete: boolean;
  loading: boolean;
  /** Call to re-fetch onboarding state (e.g. after profile image upload) */
  refresh: () => void;
}

/**
 * Hook that checks the artist's onboarding progress by querying existing endpoints.
 * Returns which features are unlocked based on their progression.
 * Call `refresh()` to re-check after a user action that might change progress.
 */
export function useOnboardingProgress(slug: string): OnboardingProgress {
  const { token } = useAuthStore();
  const [refreshKey, setRefreshKey] = useState(0);
  const [progress, setProgress] = useState<OnboardingProgress>({
    hasProfileImage: false,
    hasVerifiedTrack: false,
    hasPublishedVault: false,
    hasAtLeastOneFan: false,
    completedSteps: 0,
    totalSteps: 4,
    isComplete: false,
    loading: true,
    refresh: () => {},
  });

  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!slug) return;

    let cancelled = false;

    async function fetchProgress() {
      try {
        // Fetch all data in parallel
        const [tenantRes, tracksRes, vaultsRes, fansRes] = await Promise.all([
          resolveTenant(slug),
          listTracks(slug),
          listVaults(slug, { limit: 50 }, token),
          listFans(slug, '?limit=1'),
        ]);

        if (cancelled) return;

        // Check profile image
        let hasProfileImage = false;
        if (tenantRes.ok) {
          const tenantData = await tenantRes.json() as any;
          hasProfileImage = !!tenantData?.data?.profileUrl;
        }

        // Check verified track (passedCompliance = AI verified + Content ID accepted)
        let hasVerifiedTrack = false;
        if (tracksRes.ok) {
          const tracksData = await tracksRes.json() as any;
          const tracks = tracksData?.data || tracksData?.tracks || [];
          hasVerifiedTrack = tracks.some((t: any) => t.passedCompliance === true || t.verificationStatus === 'VERIFIED');
        }

        // Check published vault with zero credits
        let hasPublishedVault = false;
        if (vaultsRes.ok) {
          const vaultsData = await vaultsRes.json() as any;
          const vaults = vaultsData?.data || vaultsData?.vaults || [];
          hasPublishedVault = vaults.some((v: any) => {
            const s = (v.status || '').toUpperCase();
            return s === 'PUBLISHED' || s === 'SCHEDULED' || s === 'PRESSED' || s === 'OUT_OF_PRODUCTION';
          });
        }

        // Check at least one fan
        let hasAtLeastOneFan = false;
        if (fansRes.ok) {
          const fansData = await fansRes.json() as any;
          const fans = fansData?.data || fansData?.fans || [];
          const totalFans = fansData?.total || fansData?.pagination?.total || fans.length;
          hasAtLeastOneFan = totalFans > 0 || fans.length > 0;
        }

        if (cancelled) return;

        const completedSteps = [hasProfileImage, hasVerifiedTrack, hasPublishedVault, hasAtLeastOneFan]
          .filter(Boolean).length;

        setProgress({
          hasProfileImage,
          hasVerifiedTrack,
          hasPublishedVault,
          hasAtLeastOneFan,
          completedSteps,
          totalSteps: 4,
          isComplete: completedSteps === 4,
          loading: false,
          refresh,
        });
      } catch (err) {
        console.warn('[useOnboardingProgress] Error fetching progress:', err);
        if (!cancelled) {
          setProgress(prev => ({ ...prev, loading: false, refresh }));
        }
      }
    }

    fetchProgress();

    return () => { cancelled = true; };
  }, [slug, token, refreshKey, refresh]);

  return { ...progress, refresh };
}
